import { apiClient } from 'api';
import { API_ROUTES } from 'constants/routes';
import { ILocation, UpdateLocationRequest } from 'modules/locations/types';
import {
    CreateFranchisePartnerRequest,
    CreateFranchisePartnerUserRequest,
    CreateCustomExpenseRequest,
    DeleteFranchisePartnerRequestParams,
    DeleteFranchisePartnerUserRequest,
    GetFranchisePartnerConsolidatedReportRequestParams,
    GetFranchisePartnerLocationRequestParams,
    GetFranchisePartnerLocationsRequestParams,
    GetFranchisePartnerRequestParams,
    GetFranchisePartnerUsersRequestParams,
    ICustomExpense,
    IFranchisePartner,
    UpdateFranchisePartnerLocationRequestParams,
    UpdateFranchisePartnerRequest,
    UpdateFranchisePartnerRequestParams,
    UpdateFranchisePartnerUserRequest
} from 'modules/franchise_partners/types';
import { IUser } from 'modules/users/types';
import { GetAggregatedReportByFilterRequest, IConsolidatedReport } from 'modules/reports/types';

/**
 * Represents a function that retrieves a list of franchise partner locations based on the provided parameters.
 *
 * @param {GetFranchisePartnerLocationsRequestParams} pathParams - The parameters used to specify the franchise partner locations.
 * @returns {Promise<ILocation[]>} A promise that resolves to an array of locations corresponding to the franchise partner.
 */
export const getFranchisePartnerLocationsRequest = async (pathParams: GetFranchisePartnerLocationsRequestParams): Promise<ILocation[]> => {
    const response = await apiClient.get<ILocation[]>(API_ROUTES.franchise_partners.getLocations(pathParams));
    return response.data;
};

export const getFranchisePartnerLocationRequest = async (pathParams: GetFranchisePartnerLocationRequestParams): Promise<ILocation> => {
    const response = await apiClient.get<ILocation>(API_ROUTES.franchise_partners.getLocation(pathParams));
    return response.data;
};

export const updateFranchisePartnerLocationRequest = async ({
    pathParams,
    payload
}: {
    pathParams: UpdateFranchisePartnerLocationRequestParams;
    payload: UpdateLocationRequest;
}): Promise<ILocation> => {
    const response = await apiClient.put<ILocation>(API_ROUTES.franchise_partners.updateLocation(pathParams), payload);
    return response.data;
};

/**
 * Retrieves franchise partner users from the API based on the provided path parameters.
 *
 * @param {GetFranchisePartnerUsersRequestParams} pathParams - The path parameters needed to fetch the users.
 * @returns {Promise<IUser[]>} - A Promise that resolves to an array of IUser objects representing the franchise partner users.
 */
export const getFranchisePartnerUsersRequest = async (pathParams: GetFranchisePartnerUsersRequestParams): Promise<IUser[]> => {
    const response = await apiClient.get<IUser[]>(API_ROUTES.franchise_partners.getUsers(pathParams));
    return response.data;
};

export const createFranchisePartnerUserRequest = async (payload: CreateFranchisePartnerUserRequest): Promise<IUser> => {
    const response = await apiClient.post<IUser>(API_ROUTES.franchise_partners.createUser, payload);
    return response.data;
};

export const updateFranchisePartnerUserRequest = async (params: UpdateFranchisePartnerUserRequest): Promise<IUser> => {
    const response = await apiClient.put<IUser>(API_ROUTES.franchise_partners.updateUser(params), params);
    return response.data;
};

export const deleteFranchisePartnerUserRequest = async (params: DeleteFranchisePartnerUserRequest): Promise<null> => {
    const response = await apiClient.delete<null>(API_ROUTES.franchise_partners.deleteUser(params));
    return response.data;
};

/**
 * Retrieves a list of Franchise Partners from the API server.
 *
 * @returns {Promise<IFranchisePartner[]>} A Promise that resolves to an array of Franchise Partner objects.
 *
 * The function sends a GET request to the API server to fetch a list of Franchise Partners using the specified API endpoint.
 * It returns a Promise that resolves to an array of IFranchisePartner objects.
 */
export const getFranchisePartnersRequest = async (): Promise<IFranchisePartner[]> => {
    const response = await apiClient.get<IFranchisePartner[]>(API_ROUTES.franchise_partners.getFranchisePartners);
    return response.data;
};

/**
 * Function that fetches a franchise partner from the API
 *
 * @param {GetFranchisePartnerRequestParams} pathParams Parameters for fetching the franchise partner
 */
export const getFranchisePartnerRequest = async (pathParams: GetFranchisePartnerRequestParams): Promise<IFranchisePartner> => {
    const response = await apiClient.get<IFranchisePartner>(API_ROUTES.franchise_partners.getFranchisePartner(pathParams));
    return response.data;
};

/**
 * Deletes a franchise partner based on the provided path parameters.
 *
 * @param {DeleteFranchisePartnerRequestParams} pathParams - The path parameters for deleting the franchise partner.
 */
export const deleteFranchisePartnerRequest = async (pathParams: DeleteFranchisePartnerRequestParams): Promise<void> => {
    const response = await apiClient.delete<void>(API_ROUTES.franchise_partners.deleteFranchisePartner(pathParams));
    return response.data;
};

/**
 * Creates a franchise partner by sending a POST request to the specified API endpoint.
 *
 * @param {CreateFranchisePartnerRequest} payload - The data payload containing information for creating the franchise partner.
 */
export const createFranchisePartnerRequest = async (payload: CreateFranchisePartnerRequest): Promise<IFranchisePartner> => {
    const response = await apiClient.post<IFranchisePartner>(API_ROUTES.franchise_partners.createFranchisePartner, payload);
    return response.data;
};

/**
 * Asynchronously updates a franchise partner by sending a PUT request to the API endpoint.
 *
 * @param {Object} request - The request object containing path parameters and payload.
 * @param {UpdateFranchisePartnerRequestParams} request.pathParams - The path parameters for updating a franchise partner.
 * @param {UpdateFranchisePartnerRequest} request.payload - The payload data for updating a franchise partner.
 *
 */
export const updateFranchisePartnerRequest = async ({
    pathParams,
    payload
}: {
    pathParams: UpdateFranchisePartnerRequestParams;
    payload: UpdateFranchisePartnerRequest;
}): Promise<IFranchisePartner> => {
    const response = await apiClient.put<IFranchisePartner>(API_ROUTES.franchise_partners.updateFranchisePartner(pathParams), payload);
    return response.data;
};

export const getFranchisePartnerConsolidatedReportRequest = async (
    pathParams: GetFranchisePartnerConsolidatedReportRequestParams
): Promise<IConsolidatedReport> => {
    const response = await apiClient.get<IConsolidatedReport>(API_ROUTES.franchise_partners.consolidatedReport(pathParams), {
        params: pathParams
    });
    return response.data;
};

export const getAggregatedReportByFilterRequest = async (params: GetAggregatedReportByFilterRequest): Promise<IConsolidatedReport> => {
    const response = await apiClient.get<IConsolidatedReport>(API_ROUTES.franchise_partners.aggregatedReportByFilter, { params });
    return response.data;
};

/**
 * Creates a custom expense for a franchise partner.
 *
 * @param {string} franchise_partner_id - The ID of the franchise partner.
 * @param {CreateCustomExpenseRequest} payload - The custom expense data to create.
 * @returns {Promise<ICustomExpense>} A Promise that resolves to the created custom expense.
 */
export const createCustomExpenseRequest = async (
    franchise_partner_id: string,
    payload: CreateCustomExpenseRequest
): Promise<ICustomExpense> => {
    const response = await apiClient.post<ICustomExpense>(API_ROUTES.franchise_partners.createCustomExpense(franchise_partner_id), payload);
    return response.data;
};

/**
 * Updates a custom expense for a franchise partner.
 *
 * @param {string} franchise_partner_id - The ID of the franchise partner.
 * @param {string} expense_id - The ID of the custom expense to update.
 * @param {CreateCustomExpenseRequest} payload - The updated custom expense data.
 * @returns {Promise<ICustomExpense>} A Promise that resolves to the updated custom expense.
 */
export const updateCustomExpenseRequest = async (
    franchise_partner_id: string,
    expense_id: string,
    payload: CreateCustomExpenseRequest
): Promise<ICustomExpense> => {
    const response = await apiClient.put<ICustomExpense>(
        API_ROUTES.franchise_partners.updateCustomExpense(franchise_partner_id, expense_id),
        payload
    );
    return response.data;
};

/**
 * Deletes a custom expense from a franchise partner.
 *
 * @param {string} franchise_partner_id - The ID of the franchise partner.
 * @param {string} expense_id - The ID of the custom expense to delete.
 * @returns {Promise<void>} A Promise that resolves when the custom expense is deleted.
 */
export const deleteCustomExpenseRequest = async (franchise_partner_id: string, expense_id: string): Promise<void> => {
    const response = await apiClient.delete<void>(API_ROUTES.franchise_partners.deleteCustomExpense(franchise_partner_id, expense_id));
    return response.data;
};
